import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { STEPS, TOTAL_STEPS } from "@/components/diagnostic/questions";
import DiagnosticResults from "@/components/diagnostic/DiagnosticResults";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Shield, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/hooks/useSettings";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "la_diagnostic_progress";

const trackEvent = (eventName, params = {}) => {
  if (typeof window.fbq === "function") window.fbq("track", eventName, params);
  if (typeof window.gtag === "function") window.gtag("event", eventName, params);
};

export default function DiagnosticPage() {
  // Set OG meta tags for diagnostic page
  useEffect(() => {
    document.title = "Diagnostic Gratuit — Lead Artisan";
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
      if (el) el.setAttribute("content", content);
    };
    setMeta("og:title", "Diagnostic Gratuit — Avez-vous le contrôle de votre carnet de commandes ?");
    setMeta("og:description", "Faites le diagnostic de votre entreprise en quelques minutes. Découvrez où vous perdez des clients et du chiffre d'affaires.");
    setMeta("og:url", "https://lead-artisan.com/diagnostic");
    setMeta("twitter:title", "Diagnostic Gratuit — Lead Artisan");
    setMeta("twitter:description", "Faites le diagnostic de votre entreprise en quelques minutes.");
  }, []);

  // Phases: intro → contact → questions → analysis → results
  const [phase, setPhase] = useState("intro");
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contactData, setContactData] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "", consent: false });
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [contactSent, setContactSent] = useState(false);
  const [searchParams] = useSearchParams();
  const { settings } = useSettings();
  const topRef = useRef(null);
  const diagnosticId = useRef(null);

  const utm = useRef({
    utm_source: searchParams.get("utm_source") || "",
    utm_campaign: searchParams.get("utm_campaign") || "",
    utm_content: searchParams.get("utm_content") || "",
    utm_term: searchParams.get("utm_term") || "",
    utm_medium: searchParams.get("utm_medium") || "",
  });

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth" });

  // ─── Restore saved progress ───
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (saved && saved.answers && saved.contactData && saved.stepIdx >= 0) {
        setAnswers(saved.answers);
        setContactData(saved.contactData);
        setStepIdx(saved.stepIdx);
        setContactSent(saved.contactSent || false);
        diagnosticId.current = saved.diagnosticId || null;
        if (saved.contactSent) {
          setPhase("questions");
        }
      }
    } catch { /* ignore */ }
  }, []);

  // ─── Auto-save progress to localStorage ───
  useEffect(() => {
    if (phase === "questions" || phase === "contact") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        answers, contactData, stepIdx, contactSent,
        diagnosticId: diagnosticId.current,
      }));
    }
  }, [answers, contactData, stepIdx, phase, contactSent]);

  // ─── Send partial lead to CRM on page leave ───
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (contactSent && contactData.phone && phase === "questions") {
        // Use sendBeacon for reliable send on page close
        const payload = JSON.stringify({
          first_name: contactData.first_name,
          last_name: contactData.last_name,
          phone: contactData.phone,
          email: contactData.email,
          company: contactData.company,
          source: "Diagnostic Lead Artisan - Non complété",
          tags: ["diagnostic", "non_complete", `etape_${stepIdx + 1}`],
        });
        navigator.sendBeacon(`${API}/api/loomy/leads`, new Blob([payload], { type: "application/json" }));
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [contactSent, contactData, phase, stepIdx]);

  const setAnswer = useCallback((id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  }, []);

  const toggleMulti = useCallback((id, value) => {
    setAnswers((prev) => {
      const arr = prev[id] || [];
      const v = typeof value === "object" ? value.value || value : value;
      return { ...prev, [id]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] };
    });
  }, []);

  const currentStep = STEPS[stepIdx];
  const totalPhases = STEPS.length + 2; // contact + analysis
  const progress = phase === "intro" ? 0
    : phase === "contact" ? 5
    : phase === "questions" ? Math.round(10 + ((stepIdx + 1) / STEPS.length) * 75)
    : phase === "analysis" ? 90
    : 100;

  // ─── Submit contact and send partial lead to CRM immediately ───
  const handleContactSubmit = useCallback(async () => {
    if (!contactData.first_name || !contactData.last_name || !contactData.email || !contactData.phone || !contactData.company || !contactData.consent) return;
    setIsSubmitting(true);

    // Send partial lead to CRM immediately (non-blocking)
    try {
      await axios.post(`${API}/api/loomy/leads`, {
        first_name: contactData.first_name,
        last_name: contactData.last_name,
        phone: contactData.phone,
        email: contactData.email,
        company: contactData.company,
        source: "Diagnostic Lead Artisan - En cours",
        tags: ["diagnostic", "en_cours"],
      });
    } catch { /* non-blocking */ }

    setContactSent(true);
    setIsSubmitting(false);
    setPhase("questions");
    scrollTop();
    trackEvent("StartDiagnostic");
  }, [contactData]);

  // ─── Analysis animation ───
  useEffect(() => {
    if (phase !== "analysis") return;
    const labels = [
      "Analyse de l'acquisition...", "Analyse des demandes...", "Analyse des devis...",
      "Analyse du suivi...", "Analyse de la réactivité...", "Calcul du potentiel...",
      "Construction de votre plan...",
    ];
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setAnalysisStep(i);
      if (i >= labels.length) {
        clearInterval(timer);
        setTimeout(() => {
          setPhase("results");
          scrollTop();
          // Clear saved progress
          localStorage.removeItem(STORAGE_KEY);
          trackEvent("Lead", { content_name: "diagnostic" });
        }, 600);
      }
    }, 700);
    return () => clearInterval(timer);
  }, [phase]);

  // ─── Handle next step ───
  const handleNext = useCallback(() => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx((p) => p + 1);
      scrollTop();
      trackEvent("DiagnosticStep", { step: stepIdx + 2, step_name: STEPS[stepIdx + 1]?.id });
    } else {
      // Final step → submit and analyze
      setPhase("analysis");
      scrollTop();
      trackEvent("CompleteDiagnostic");
      (async () => {
        try {
          const res = await axios.post(`${API}/api/diagnostic/submit`, {
            answers,
            ...utm.current,
          });
          setResult(res.data);
          diagnosticId.current = res.data.id;

          // Update CRM with complete lead + diagnostic info
          if (res.data.id) {
            await axios.post(`${API}/api/diagnostic/${res.data.id}/contact`, contactData);
          }
        } catch {
          // Still show results if available
        }
      })();
    }
  }, [stepIdx, answers, contactData]);

  const handleBack = useCallback(() => {
    if (stepIdx > 0) { setStepIdx((p) => p - 1); scrollTop(); }
  }, [stepIdx]);

  const logoUrl = settings?.logo_light || "/logo_transparent.png";

  // ─── INTRO ───
  if (phase === "intro") {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" ref={topRef}>
        <div className="max-w-2xl w-full text-center py-12">
          {logoUrl && <img src={logoUrl} alt="Lead Artisan" className="h-12 mx-auto mb-8 opacity-80" />}
          <div className="inline-flex items-center gap-1.5 bg-accent/20 text-accent px-3 py-1.5 rounded-md text-xs font-semibold mb-6">
            <Shield className="w-3.5 h-3.5" />
            <span className="uppercase tracking-wider">Diagnostic gratuit</span>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white leading-tight mb-6">
            Avez-vous vraiment le contrôle de votre{" "}
            <span className="text-accent">carnet de commandes ?</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-400 mb-10 max-w-xl mx-auto">
            Faites le diagnostic de votre entreprise en quelques minutes et découvrez où vous perdez potentiellement des clients, du temps et du chiffre d'affaires.
          </p>
          <Button
            onClick={() => { setPhase("contact"); scrollTop(); }}
            data-testid="start-diagnostic"
            className="bg-accent hover:bg-orange-600 text-white font-bold text-base sm:text-lg px-8 py-5 sm:py-6 rounded-md transition-all hover:scale-105 shadow-lg"
          >
            Faire mon diagnostic gratuitement
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-xs text-slate-500 mt-6">Durée estimée : 4–6 minutes</p>
        </div>
      </div>
    );
  }

  // ─── CONTACT (collected FIRST) ───
  if (phase === "contact") {
    return (
      <Shell progress={5} logoUrl={logoUrl} ref={topRef}>
        <div className="max-w-lg mx-auto py-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <User className="w-7 h-7 text-primary" />
            </div>
            <h2 className="text-2xl font-black text-slate-900 mb-2">Commençons par vous connaître</h2>
            <p className="text-slate-600 text-sm">Ces informations nous permettent de personnaliser votre diagnostic.</p>
          </div>
          <div className="space-y-4">
            {[
              { id: "first_name", label: "Prénom", placeholder: "Jean-Marc" },
              { id: "last_name", label: "Nom", placeholder: "Favre" },
              { id: "company", label: "Entreprise", placeholder: "Favre Plomberie Sàrl" },
              { id: "email", label: "E-mail", placeholder: "jean-marc@exemple.ch", type: "email" },
              { id: "phone", label: "Téléphone", placeholder: "+41 79 123 45 67", type: "tel" },
            ].map((f) => (
              <div key={f.id}>
                <label className="block text-sm font-semibold text-slate-700 mb-1">{f.label} *</label>
                <input
                  type={f.type || "text"}
                  value={contactData[f.id]}
                  onChange={(e) => setContactData((p) => ({ ...p, [f.id]: e.target.value }))}
                  placeholder={f.placeholder}
                  data-testid={`contact-${f.id}`}
                  className="w-full border-2 border-slate-200 rounded-md px-4 py-3 text-sm focus:border-primary focus:outline-none transition-colors"
                />
              </div>
            ))}
            <label className="flex items-start gap-2 mt-4 cursor-pointer">
              <input type="checkbox" checked={contactData.consent} onChange={(e) => setContactData((p) => ({ ...p, consent: e.target.checked }))} className="mt-1" data-testid="contact-consent" />
              <span className="text-xs text-slate-500">J'accepte que mes données soient utilisées pour mon diagnostic personnalisé et pour être contacté par Lead Artisan. Données traitées conformément à la LPD suisse.</span>
            </label>
            <Button
              onClick={handleContactSubmit}
              disabled={isSubmitting || !contactData.consent || !contactData.first_name || !contactData.last_name || !contactData.email || !contactData.phone || !contactData.company}
              data-testid="submit-contact"
              className="w-full bg-accent hover:bg-orange-600 text-white font-bold text-base py-5 rounded-md mt-4 transition-all hover:scale-[1.02]"
            >
              {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              Commencer mon diagnostic
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  // ─── ANALYSIS ───
  if (phase === "analysis") {
    const labels = [
      "Analyse de l'acquisition...", "Analyse des demandes...", "Analyse des devis...",
      "Analyse du suivi...", "Analyse de la réactivité...", "Calcul du potentiel...",
      "Construction de votre plan...",
    ];
    return (
      <Shell progress={90} logoUrl={logoUrl} ref={topRef}>
        <div className="max-w-md mx-auto text-center py-16">
          <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-8"></div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {contactData.first_name}, votre diagnostic est en cours...
          </h2>
          <p className="text-sm text-slate-500 mb-6">Nous analysons vos réponses</p>
          <div className="space-y-3 text-left">
            {labels.map((label, i) => (
              <div key={label} className={`flex items-center gap-3 transition-all duration-500 ${i <= analysisStep ? "opacity-100" : "opacity-20"}`}>
                {i < analysisStep ? (
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                ) : i === analysisStep ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0" />
                ) : (
                  <div className="w-5 h-5 border-2 border-slate-200 rounded-full flex-shrink-0" />
                )}
                <span className={`text-sm font-medium ${i <= analysisStep ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </Shell>
    );
  }

  // ─── RESULTS ───
  if (phase === "results" && result) {
    return <DiagnosticResults result={result} answers={answers} contact={contactData} />;
  }

  // ─── QUESTIONNAIRE ───
  return (
    <Shell progress={progress} logoUrl={logoUrl} ref={topRef}>
      <div className="max-w-2xl mx-auto py-6">
        <p className="section-label text-primary mb-2">Étape {stepIdx + 1} / {STEPS.length}</p>
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 mb-1">{currentStep.title}</h2>
        <p className="text-sm text-slate-500 mb-8">{currentStep.subtitle}</p>

        <div className="space-y-6">
          {currentStep.questions.map((q) => {
            if (q.condition && !q.condition(answers)) return null;
            return <QuestionField key={q.id} q={q} answers={answers} setAnswer={setAnswer} toggleMulti={toggleMulti} />;
          })}
        </div>

        <div className="flex justify-between mt-10 gap-4">
          <Button variant="outline" onClick={handleBack} disabled={stepIdx === 0} className="border-2 border-slate-200 px-6 py-3">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
          <Button onClick={handleNext} data-testid="next-step" className="bg-primary hover:bg-blue-800 text-white font-bold px-6 py-3">
            {stepIdx === STEPS.length - 1 ? "Voir mon diagnostic" : "Continuer"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </Shell>
  );
}

// ─── Shell ───
const Shell = React.forwardRef(({ progress, logoUrl, children }, ref) => (
  <div className="min-h-screen bg-white" ref={ref}>
    <div className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
        {logoUrl && <img src={logoUrl} alt="Lead Artisan" className="h-8 w-auto" />}
        <span className="text-xs font-semibold text-slate-500">Diagnostic {progress}%</span>
      </div>
      <div className="h-1 bg-slate-100">
        <div className="h-1 bg-accent transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
      </div>
    </div>
    <div className="max-w-3xl mx-auto px-4">{children}</div>
  </div>
));

// ─── Question Field ───
function QuestionField({ q, answers, setAnswer, toggleMulti }) {
  const val = answers[q.id];

  if (q.type === "text" || q.type === "number" || q.type === "email" || q.type === "tel") {
    return (
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{q.label}</label>
        <input
          type={q.type === "number" ? "number" : "text"}
          value={val || ""}
          onChange={(e) => setAnswer(q.id, e.target.value)}
          placeholder={q.placeholder}
          data-testid={`q-${q.id}`}
          className="w-full border-2 border-slate-200 rounded-md px-4 py-3 text-sm focus:border-primary focus:outline-none"
        />
      </div>
    );
  }

  if (q.type === "select") {
    return (
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{q.label}</label>
        <select
          value={val || ""}
          onChange={(e) => setAnswer(q.id, e.target.value)}
          data-testid={`q-${q.id}`}
          className="w-full border-2 border-slate-200 rounded-md px-4 py-3 text-sm focus:border-primary focus:outline-none bg-white"
        >
          <option value="">Sélectionnez...</option>
          {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  if (q.type === "pills") {
    return (
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-3">{q.label}</label>
        <div className="flex flex-wrap gap-2">
          {q.options.map((o) => {
            const v = typeof o === "object" ? o.value : o;
            const l = typeof o === "object" ? o.label : o;
            const active = val === v;
            return (
              <button key={v} onClick={() => setAnswer(q.id, v)} data-testid={`q-${q.id}-${v}`}
                className={`px-4 py-2.5 rounded-md text-sm font-medium border-2 transition-all ${
                  active ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}>
                {l}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (q.type === "cards") {
    return (
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-3">{q.label}</label>
        <div className="grid gap-2">
          {q.options.map((o) => {
            const v = typeof o === "object" ? o.value : o;
            const l = typeof o === "object" ? o.label : o;
            const active = val === v;
            return (
              <button key={v} onClick={() => setAnswer(q.id, v)} data-testid={`q-${q.id}-${v}`}
                className={`text-left px-4 py-3 rounded-md border-2 text-sm font-medium transition-all ${
                  active ? "border-accent bg-accent/5 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}>
                {active && <CheckCircle2 className="w-4 h-4 text-accent inline mr-2" />}
                {l}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (q.type === "multi") {
    const selected = val || [];
    return (
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-3">{q.label}</label>
        <div className="grid sm:grid-cols-2 gap-2">
          {q.options.map((o) => {
            const v = typeof o === "object" ? o.value : o;
            const l = typeof o === "object" ? o.label : o;
            const active = selected.includes(v);
            return (
              <button key={v} onClick={() => toggleMulti(q.id, v)} data-testid={`q-${q.id}-${v}`}
                className={`text-left px-4 py-2.5 rounded-md border-2 text-sm transition-all ${
                  active ? "border-primary bg-primary/5 text-primary font-semibold" : "border-slate-200 text-slate-600 hover:border-slate-300"
                }`}>
                {active ? <CheckCircle2 className="w-4 h-4 inline mr-2" /> : <div className="w-4 h-4 border-2 border-slate-300 rounded inline-block mr-2 align-text-bottom" />}
                {l}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}
